import type { SeverityLevel } from "@sentry/nextjs";
import * as Sentry from "@sentry/nextjs";

export interface ErrorContext {
  userId?: string;
  organizationId?: string;
  feature?: string;
  action?: string;
  metadata?: Record<string, unknown>;
}

function applyContextToScope(scope: Sentry.Scope, context?: ErrorContext) {
  if (context) {
    if (context.userId) scope.setUser({ id: context.userId });
    if (context.organizationId) scope.setTag("organizationId", context.organizationId);
    if (context.feature) scope.setTag("feature", context.feature);
    if (context.action) scope.setTag("action", context.action);
    if (context.metadata) {
      for (const [key, value] of Object.entries(context.metadata)) {
        scope.setExtra(key, value);
      }
    }
  }
}

export function captureError(error: Error | unknown, context?: ErrorContext, level: SeverityLevel = "error") {
  if (process.env.NODE_ENV === "development") {
    console.error("Error captured:", error, context);
  }

  Sentry.withScope((scopeInstance) => {
    scopeInstance.setLevel(level);
    applyContextToScope(scopeInstance, context);

    if (error instanceof Error) {
      scopeInstance.setTag("errorType", "javascript");
      scopeInstance.setExtra("errorName", error.name);
      scopeInstance.setExtra("errorStack", error.stack);
    } else {
      scopeInstance.setTag("errorType", "unknown");
      scopeInstance.setExtra("rawError", error);
    }

    Sentry.captureException(error);
  });
}

export function setUserContext(user: {
  id: string;
  email?: string | undefined;
  username?: string | undefined;
  organizationId?: string | undefined;
  organizationName?: string | undefined;
}) {
  // Use spread operator to conditionally include properties only when they have values
  // This avoids the exactOptionalPropertyTypes issue with undefined
  Sentry.setUser({
    id: user.id,
    ...(user.email && { email: user.email }),
    ...(user.username && { username: user.username }),
  });

  if (user.organizationId) {
    Sentry.setTag("organizationId", user.organizationId);
  }

  if (user.organizationName) {
    Sentry.setTag("organizationName", user.organizationName);
  }
}

export function clearUserContext() {
  Sentry.setUser(null);
}
